<template>
  <div>
    <div class="caption-text">
      <p>Googleのアカウントを使って一括で</p>
      <p>「{{ brandName }}」に店舗を登録する</p>
    </div>
    <div class="initial-registration__store-sites">
      <Info>
        <p>このアカウントで設定されているビジネスグループに属する店舗が一括で登録できます</p>
      </Info>
      <Info v-if="showGmbOauthLink">
        <p class="c-gmb-account-select-info">
          「アカウントを選び直す」が動作しない場合・ビジネスグループが表示されない場合は、
          Googleアカウント > セキュリティ > アカウントにアクセスできるサードパーティ アプリ [
          <a href="https://myaccount.google.com/permissions" target="_blank">
            https://myaccount.google.com/permissions
          </a>
          ]から本サービスのアクセス権を削除してから再度お試しください。
        </p>
      </Info>
      <form @submit.prevent="handleSubmit(onSubmit)">
        <div class="p-registration__body__form">
          <p class="c-formLabel">
            連携するビジネスグループ
          </p>
          <div class="p-registration__body__select">
            <Select
              name="business_groups"
              :options="businessGroups"
              :model-value="selectedBusinessGroupId"
              @update:modelValue="onChangeBusinessGroup"
            />
          </div>
        </div>
      </form>
    </div>

    <div v-if="selectedBusinessGroupId">
      <div v-if="locationsLoading" class="loading-center">
        <GmbPolling />
        <Loading />
      </div>
      <div v-else>
        <template v-if="locationsCollection.length === 0">
          <div class="p-filters__item p-filters__item__s__12 p-filters__item__m__4 p-filters__item__m__9 filter-items">
            <p>Google マップのロケーションが存在しません。</p>
            <p>Google マップでロケーションを作成後、この画面を再読み込みしてください。</p>
            <p>
              Google マップのリンクは
              <span class="c-links__item u-pad0">
                <a href="https://www.google.com/intl/ja_jp/business/" target="_blank">こちら</a>
              </span>
            </p>
          </div>
        </template>
        <template v-else>
          <p class="location-select">
            連携する店舗を選択しましょう。
          </p>
          <div class="p-filters__item p-filters__item__s__12 p-filters__item__m__4 p-filters__item__m__9 filter-items">
            <FilterItem
              :list="locationsCollection"
              :list-type="'location'"
              store-name="companiesStoreGmbLocations"
              value-key="id"
              filter-type="location_ids"
              head-text="全てを選択"
              field-class=""
            />
          </div>
          <div class="initial-registration-brands-location__bottom" :class="{'c-error-msg': isOverLocationCount}">
            <p class="available-store-amounts">登録可能な店舗数：{{ maxLocationCount }}店舗中 {{ selectedLocationCount }}店舗が選択されています</p>
            <div class="c-button c-button--center">
              <a v-if="showGmbOauthLink" class="c-button__item c-button__item--outline-cancel" :href="`/google_oauth/authorization/new?company_uuid=${ currentCompanyUuid }`">
                アカウントを選び直す
              </a>
              <button class="c-button__item c-button__item--outline-cancel" type="submit" @click="onBack">
                戻る
              </button>
              <button :disabled="!canSubmit" :class="{'c-button__item--disabled': !canSubmit}" class="c-button__item c-button__item--primary" type="submit" @click="onSubmit">
                保存する
              </button>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>


<script>
import FilterItem from '../components/FilterItem'
import {mapActions, mapMutations, mapState, mapGetters} from 'vuex'
import Loading from '../components/Loading'
import Info from '../components/Info'
import Select from '../components/Select'
import GmbPolling from '../components/GmbPolling'

export default {
  name: 'GMBLocations',
  components: {
    Loading,
    FilterItem,
    Info,
    Select,
    GmbPolling,
  },
  props: {
    maxLocationCount: Number,
    brandName: String,
    businessGroups: Object,
    showGmbOauthLink: Boolean
  },
  emits: ['submit-success', 'on-back'],
  data() {
    return {
      selectedBusinessGroupId: null
    }
  },
  computed: {
    ...mapState({
      locationsLoading: state => state.companiesStoreGmbLocations.loading,
      formValues: state => state.companiesStoreGmbLocations.formValues,
    }),
    ...mapGetters({
      notConnectedLocations: 'companiesStoreGmbLocations/notConnectedLocations',
    }),
    currentCompanyUuid: function () {
      return this.$route.params.company_uuid
    },
    locationsCollection() {
      if(!this.notConnectedLocations) return []

      return this.notConnectedLocations.map(v => {
        const storeCode = v.attributes.store_code
        return {
          id: v.id,
          name: v.attributes.name + (storeCode ? ` (${storeCode})` : '')
        }
      })
    },
    selectedLocationCount() {
      return this.formValues.location_ids.length
    },
    isOverLocationCount() {
      return this.selectedLocationCount > this.maxLocationCount
    },
    canSubmit() {
      return !this.isOverLocationCount && this.selectedLocationCount !== 0
    },
  },
  methods: {
    ...mapActions({
      readLocations: 'companiesStoreGmbLocations/readStoreGmbLocations',
      writeLocations: 'companiesStoreGmbLocations/writeStoreGmbLocations',
    }),
    ...mapMutations({
      setFormValues: 'companiesStoreGmbLocations/setFormValues',
    }),
    onChangeBusinessGroup(value) {
      this.selectedBusinessGroupId = value

      const payload = {
        companyUuid: this.currentCompanyUuid,
        accountId: this.selectedBusinessGroupId,
      }
      //ロケーションapiのコール
      this.readLocations(payload)
    },
    async onSubmit() {
      if (this.selectedLocationCount === 0) {
        alert('１店舗以上選択してください')
        return
      }
      if (this.isOverLocationCount) {
        alert('店舗を減らしてください。')
        return
      }
      const payload = {
        companyUuid: this.currentCompanyUuid,
        brandId: this.$route.params.brand_id
      }
      const isSuccess = await this.writeLocations(payload)
      if (isSuccess) {
        this.$router.push(`/s/companies/${this.currentCompanyUuid}/location/stores`)
        this.$emit('submit-success')
      }
    },
    onBack() {
      this.$emit('on-back')
    },
  },
}
</script>

<style lang="scss" scoped>
.caption-text {
  text-align: center;
}
.filter-items {
  border: 1px solid #E4E7EB;
  border-radius: 4px;
  margin: 64px auto 32px;
  padding: 25px 32px;
  text-align: left;
}
.initial-registration-brands-location__bottom {
  border-top: 1px solid #E4E7EB;
  padding: 24px 0;
  display: flex;
  justify-content: flex-end;
  align-items: baseline;
}
.available-store-amounts {
  margin-right: 2%;
}
.c-error-msg {
  font-weight: bold;
}
.p-filters__item {
  margin: 32px auto;
  max-width: 512px;
}
.initial-registration__store-sites {
  margin: 32px auto 0;
  max-width: 512px;
}
.location-select {
  margin-top: 32px;
  text-align: center;
}
.c-gmb-account-select-info {
  text-align: left;
}
.loading-center {
  margin-top: 10px;
  text-align: center;
}
</style>
